import React, { useMemo, useState, useRef, useLayoutEffect, useCallback, memo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { groupBy } from 'lodash';

import { Box, Button, Dimmer } from '~Styles';
import { useButton, useDeviceInfo, useI18n, useLogger, useSelector } from '~Hooks';
import { useCart } from '~Components/CartProvider';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useStaffContext } from '~Scenes/Handy/Context/StaffProvider';
import { BaseButton } from '~Scenes/Handy/styles';
import CartHeader from './CartHeader';
import CartActions from './CartActions';
import { combineArticles } from '~Utils/combineArticles';
import useConfirm from '~Hooks/useConfirm';
import compressIcon from '~Assets/icons/compress.svg';
import expandIcon from '~Assets/icons/expand.svg';
import trashIcon from '~Assets/icons/delete_outline.svg';
import { useStorage } from '~Components/Storage';
import Icon from '~ComponentsImproved/Icon';
import { useApolloClient } from '@apollo/client';
import { usePage } from '~Scenes/Handy/Context/PageProvider';
import TypeChanger from '~ComponentsImproved/TSR/TypeChanger';
import CartItem, { CartItemName, CartItemWrapper } from './CartItem';
import OfferItemWrapper from '~Scenes/POS/Components/Cart/Components/OfferItemWrapper';
import { CompleteBongQuery_venue_company_categories_items as BongQueryItem } from '~Scenes/POS/Queries/__queries__';
import { groupOfferMatches } from '~Utils/offers';
import { Item as ChequeItem } from '@baemingo/table-events/dist/types';
import ChequeItems from './ChequeItems';

const Container = styled.div`
  position: absolute;
  box-sizing: border-box;
  width: 100%;
  height: 70px;
  bottom: 0px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  padding-right: 8px;
  z-index: 9;
`;

const CartElement = styled.div<{ isPhone?: boolean; expanded: boolean }>`
  background: var(--colorBlueGray1300);
  color: var(--colorWhite);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2), 0 0 12px rgba(0, 0, 0, 0.1);
  height: 392px;
  width: calc(100vw - 97px);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  z-index: 2;
  padding-bottom: ${({ isPhone }) => (isPhone ? '32px' : '0')};
  transition: transform 0.15s cubic-bezier(0.215, 0.61, 0.355, 1);
  display: flex;
  flex-direction: column;

  ${({ expanded }) =>
    expanded
      ? css`
          transform: translateY(calc(100% - 714px));
        `
      : css`
          transform: translateY(calc(100% - 392px));
        `}
`;

const BottomCartActions = styled.div`
  position: absolute;
  bottom: 8px;
  right: 16px;
  z-index: 2;
  display: flex;
  gap: 8px;
`;

const CompressButton = styled(BaseButton)`
  width: auto;
  padding: 4px;
  border: 1px solid var(--colorBlueGray1100);
  background: var(--colorBlueGray1300);
  border-radius: 4px;
`;

const WaitButton = styled(Button)`
  max-height: 32px;
`;

const CartItemList = styled.div`
  overflow-y: scroll;
  transition: all 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
  padding-bottom: 40px; /* Increased to ensure enough space for bottom content */
  flex: 1;
  height: auto;
  min-height: 82px;
  -webkit-overflow-scrolling: touch;
`;

const GroupTitle = styled.div<{ color?: string }>`
  background: ${({ color }) => color};
  font-size: 12px;
  font-weight: 600;
  width: 100%;
  color: var(--colorBlueGray1300);
  padding-bottom: 1px;
  box-sizing: border-box;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Group = styled.div<{ color?: string }>`
  padding-bottom: 8px;
  border-left: 3px solid transparent;
  border-color: ${({ color }) => color};

  /** No gap below the last group, so the new items sit flush against the cheque items */
  &:last-child {
    padding-bottom: 0;
  }
`;

/** Holds the new (not yet sent) items, so the last Group can be targeted with :last-child */
const NewItems = styled.div``;

const RemoveMessagesButton = styled(Button)`
  height: 24px;
  padding: 0;
  width: 24px;
  min-height: 24px;
  background-color: transparent;
  min-width: 0;
`;

interface Props {
  onSubmit: () => void;
  onCancel: () => void;
  handleItemPress: (
    item: BongQueryItem,
    manualPrice?: number,
    volume?: number,
    message?: string,
  ) => void;
  setSubPage: (subPage: any) => void;
  loading?: boolean;
  /** Items already on the cheque (TSR only), rendered dimmed below the cart items */
  chequeItems?: ChequeItem[];
}

const Cart: React.FC<Props> = ({
  onSubmit,
  onCancel,
  handleItemPress,
  setSubPage,
  loading,
  chequeItems,
}) => {
  const cart = useCart();
  const logger = useLogger('handy');
  const deviceInfo = useDeviceInfo();
  const { i18n } = useI18n();
  const [showCourseTypeModal, setShowCourseTypeModal] = useState(false);
  const [cartExpanded, setCartExpanded] = useState(false);
  const bottomActionsRef = useRef<HTMLDivElement>(null);
  const { authedStaff } = useStaffContext();
  const { selectedCheque } = useChequeContext();
  const { selectedTable } = useTableContext();
  const { confirm } = useConfirm();
  const [storage, setStorage] = useStorage();
  const client = useApolloClient();
  const venueId = useSelector((state) => state.venueId);
  const page = usePage();
  const isQSR = page?.current === 'POS';

  const updateBottomActionsStyle = (expanded: boolean) => {
    if (bottomActionsRef.current) {
      bottomActionsRef.current.style.transition = `transform 0.1s cubic-bezier(0.215, 0.61, 0.355, 1) ${
        expanded ? '0.2s' : '0s'
      }`;
      bottomActionsRef.current.style.transform = expanded ? 'translateY(0px)' : 'translateY(68px)';
    }
  };

  const toggleCartExpanded = useCallback((expanded: boolean) => {
    setCartExpanded(expanded);
    updateBottomActionsStyle(expanded);
  }, []);

  // Update styles when component mounts
  useLayoutEffect(() => {
    updateBottomActionsStyle(cartExpanded);
  }, []);

  const dimmerClickHandlers = useButton({
    onClick: () => {
      cart.setSelectedItems([]);
      toggleCartExpanded(false);
    },
  });

  const handleClear = async () => {
    let confirmed = true;
    // If there are more than 5 items in the cart, confirm the clear
    if (cart.items.length > 5) {
      confirmed = false;
      confirmed = await confirm(i18n('Handy.Ordering.Clear.Confirm'));
    }

    if (!confirmed) {
      return;
    }

    logger.info('User clear in Order cart', {
      items: cart.packagedItems,
      selectedCheque,
      selectedTable,
      authedStaff,
    });
    cart.clear();
    cart.clearUserData();
    toggleCartExpanded(false);
  };

  const toggleCompress = () => {
    // Clear item selection as we toggle compression
    cart.setSelectedItems([]);
    setStorage({ compressedCart: !storage.compressedCart });
  };

  // Match offers using raw cart items (like POS does), not packaged items
  const grouped = useMemo(() => groupOfferMatches(cart.items, venueId!, client, i18n), [
    cart.items,
    venueId,
    client,
    i18n,
  ]);

  // Apply compression to unmatched items for display
  const unmatchedItemsList = useMemo(() => {
    const unmatchedPackaged = grouped.unmatched
      .map((item) => cart.packagedItems.find((pi) => pi.id === item.id))
      .filter(Boolean) as typeof cart.packagedItems;

    return storage.compressedCart ? combineArticles(unmatchedPackaged) : unmatchedPackaged;
  }, [grouped.unmatched, cart.packagedItems, storage.compressedCart]);

  const groupedItems = useMemo(() => {
    return groupBy(unmatchedItemsList, (item) => item.courseType?.name ?? 'NO_COURSE_TYPE');
  }, [unmatchedItemsList]);

  const sortedGroups = useMemo(() => {
    return Object.keys(groupedItems).sort((a, b) => {
      if (a === 'NO_COURSE_TYPE') return -1;
      if (b === 'NO_COURSE_TYPE') return 1;

      const orderA: number = groupedItems[a][0]?.courseType?.order ?? Infinity;
      const orderB: number = groupedItems[b][0]?.courseType?.order ?? Infinity;
      return orderA - orderB;
    });
  }, [groupedItems]);

  const waitButton = useButton({
    onClick: () => {
      setShowCourseTypeModal(true);
    },
  });

  const clearMessagesButton = useButton({
    onClick: () => {
      cart.removeMessagesFromTicket();
    },
  });

  const onCartExpanded = useCallback(() => {
    cart.setSelectedItems([]);
    toggleCartExpanded(!cartExpanded);
  }, [cart.setSelectedItems, toggleCartExpanded, cartExpanded]);

  return (
    <>
      <Dimmer {...dimmerClickHandlers} amount="low" active={cartExpanded} />
      <Container data-testid="cart-container">
        <CartActions
          loading={loading}
          onSend={() => {
            onSubmit();
            toggleCartExpanded(false);
          }}
          onClear={handleClear}
          onCancel={onCancel}
          handleItemPress={handleItemPress}
          setSubPage={setSubPage}
          cartOpen={cartExpanded}
        />
        <CartElement data-testid="cart" isPhone={deviceInfo?.isPhone} expanded={cartExpanded}>
          <CartHeader
            title={
              cart.packagedItems.length + ' ' + i18n('Handy.NrOfItems', cart.packagedItems.length)
            }
            total={cart.total}
            discount={cart.discount}
            cartExpanded={cartExpanded}
            onCartExpanded={onCartExpanded}
          />
          <CartItemList>
            <NewItems>
              {sortedGroups.map((key) => {
                const color = groupedItems[key][0].courseType?.color;
                return (
                  <Group key={key} color={color}>
                    {key !== 'NO_COURSE_TYPE' && <GroupTitle color={color}>{key}</GroupTitle>}
                    {groupedItems[key].map((item) => {
                      return (
                        <CartItem
                          key={item.id}
                          item={item}
                          compressedCart={storage.compressedCart}
                          isSelected={cart.selectedItems.has(item.id)}
                          toggleSelectedItem={cart.toggleSelectedItem}
                        />
                      );
                    })}
                  </Group>
                );
              })}
              {grouped.matches.map((offerMatch, index) => {
                // Calculate total old price (sum of all item prices without offer discount)
                const oldPrice = offerMatch.items.reduce((sum, item) => {
                  const packagedItem = cart.packagedItems.find((pi) => pi.id === item.id);
                  return sum + (packagedItem?.amount ?? 0);
                }, 0);

                // Calculate total new price (sum of all item prices with offer discount applied)
                const newPrice = offerMatch.items.reduce((sum, item) => {
                  const packagedItem = cart.packagedItems.find((pi) => pi.id === item.id);
                  return sum + ((packagedItem?.amount ?? 0) - (packagedItem?.discount ?? 0));
                }, 0);

                return (
                  <OfferItemWrapper
                    key={index}
                    offerId={offerMatch.offerId}
                    oldPrice={oldPrice}
                    newPrice={newPrice}>
                    {offerMatch.items.map((item) => {
                      // Find the packaged version of this item for proper price/discount display
                      const packagedItem = cart.packagedItems.find((pi) => pi.id === item.id);
                      if (!packagedItem) return null;

                      return (
                        <CartItem
                          key={item.id}
                          item={packagedItem}
                          compressedCart={storage.compressedCart}
                          isSelected={cart.selectedItems.has(item.id)}
                          toggleSelectedItem={cart.toggleSelectedItem}
                          hidePrice={true}
                        />
                      );
                    })}
                  </OfferItemWrapper>
                );
              })}

              {cart.ticketMessages.length > 0 && (
                <Group color="var(--colorBlueGray400)">
                  <GroupTitle color="var(--colorBlueGray400)">
                    {i18n('POS.Messages')}
                    <RemoveMessagesButton {...clearMessagesButton}>
                      <Icon src={trashIcon} color="var(--colorBlueGray1300)" />
                    </RemoveMessagesButton>
                  </GroupTitle>
                  {cart.ticketMessages.map((message) => {
                    return (
                      <CartItemWrapper selected={false} key={message.id}>
                        <Box
                          display="flex"
                          alignItems="center"
                          flexDirection="row"
                          justifyContent="space-between">
                          <CartItemName>{message.message}</CartItemName>
                        </Box>
                      </CartItemWrapper>
                    );
                  })}
                </Group>
              )}
            </NewItems>

            {chequeItems && (
              <ChequeItems items={chequeItems} compressedCart={storage.compressedCart} />
            )}
          </CartItemList>
        </CartElement>
        <BottomCartActions ref={bottomActionsRef}>
          {!isQSR && (
            <WaitButton size="small" {...waitButton}>
              {i18n('POS.Wait')}
            </WaitButton>
          )}
          <CompressButton onClick={toggleCompress}>
            <Icon
              src={storage.compressedCart ? expandIcon : compressIcon}
              color="var(--colorBlueGray100)"
            />
          </CompressButton>
        </BottomCartActions>
      </Container>

      {showCourseTypeModal && (
        <TypeChanger
          onChange={(courseType) => {
            cart.addMessageToTicket(
              'wait' + courseType.id,
              i18n('POS.Wait') + ' ' + courseType.label,
              courseType.id,
            );
            setShowCourseTypeModal(false);
          }}
          onClose={() => setShowCourseTypeModal(false)}
        />
      )}
    </>
  );
};

export default memo(Cart);
